<template>
  <section class="invoice-create-page">
    <div class="invoice-create-top-grid payment-receipt-detail-top-grid">
      <article class="invoice-create-card invoice-create-card--accent-billing payment-receipt-detail-card">
        <header class="invoice-create-card__head">
          <h3>{{ text.detail.cards.operation.title }}</h3>
        </header>

        <div class="payment-receipt-detail-operation-split">
          <section class="payment-receipt-detail-operation-pane">
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.status }}</dt>
                <dd>
                  <span class="app-status-pill" :class="statusClass">{{ statusLabel }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row payment-receipt-detail-row--customer">
                <dt>{{ text.detail.cards.operation.fields.customer }}</dt>
                <dd>
                  <router-link
                    v-if="customerRoute"
                    :to="customerRoute"
                    class="payment-receipt-detail-link"
                  >
                    {{ customerName }}
                  </router-link>
                  <span v-else>{{ customerName }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row payment-receipt-detail-row--multiline">
                <dt>{{ text.detail.cards.operation.fields.business }}</dt>
                <dd>
                  <template v-if="businessLines.length">
                    <div
                      v-for="line in businessLines"
                      :key="line.key"
                      class="payment-receipt-detail-multiline-item"
                      :title="line.label"
                    >
                      <router-link
                        v-if="line.businessRoute"
                        :to="line.businessRoute"
                        class="payment-receipt-detail-link"
                      >
                        {{ line.label }}
                      </router-link>
                      <span v-else>{{ line.label }}</span>
                    </div>
                  </template>
                  <span v-else>{{ fallback }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.sent }}</dt>
                <dd>{{ sentLabel }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.lastSent }}</dt>
                <dd>{{ lastSentLabel }}</dd>
              </div>
            </dl>
          </section>

          <section class="payment-receipt-detail-operation-pane payment-receipt-detail-operation-pane--totals">
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row payment-receipt-detail-row--total payment-receipt-detail-row--total-highlight">
                <dt>{{ text.detail.cards.operation.fields.total }}</dt>
                <dd>{{ totalLabel }}</dd>
              </div>
            </dl>
          </section>
        </div>
      </article>

      <article class="invoice-create-card invoice-create-card--accent-info payment-receipt-detail-card">
        <header class="invoice-create-card__head">
          <h3>{{ summaryTitle }}</h3>

          <button
            v-if="canToggleSummary"
            type="button"
            class="payment-receipt-detail-summary-toggle"
            @click="showFullSummary = !showFullSummary"
          >
            <i class="fa" :class="showFullSummary ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
            {{ showFullSummary ? summaryToggleLessLabel : summaryToggleMoreLabel }}
          </button>
        </header>

        <dl class="payment-receipt-detail-list">
          <div
            v-if="showReceiverRfc || showReceiverBusinessName"
            class="payment-receipt-detail-row payment-receipt-detail-row--dual payment-receipt-detail-row--separator"
          >
            <div>
              <dt>{{ text.detail.cards.fiscalSummary.fields.receiverRfc }}</dt>
              <dd>{{ showReceiverRfc ? receiverRfc : '' }}</dd>
            </div>

            <div>
              <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
              <dd>{{ showReceiverBusinessName ? receiverBusinessName : '' }}</dd>
            </div>
          </div>

          <div
            v-if="showFolio || showCfdiFolio"
            class="payment-receipt-detail-row payment-receipt-detail-row--dual"
          >
            <div v-if="showFolio">
              <dt>{{ text.detail.cards.fiscalSummary.fields.folio }}</dt>
              <dd>{{ folio }}</dd>
            </div>

            <div v-if="showCfdiFolio">
              <dt>{{ text.detail.cards.fiscalSummary.fields.cfdiFolio }}</dt>
              <dd>{{ cfdiFolio }}</dd>
            </div>
          </div>

          <div v-if="showEmissionDate" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.issueDate }}</dt>
            <dd>{{ emissionDateLabel }}</dd>
          </div>

          <div v-if="showCfdiUuid" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.uuid }}</dt>
            <dd>{{ cfdiUuid }}</dd>
          </div>

          <div v-if="showCurrencyExchange" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.currencyExchange }}</dt>
            <dd>{{ currencyExchange }}</dd>
          </div>
        </dl>

        <section class="payment-receipt-detail-summary-payment-block">
          <div class="payment-receipt-detail-summary-split">
            <section class="payment-receipt-detail-summary-pane">
              <dl class="payment-receipt-detail-list">
                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.paymentAccount }}</dt>
                  <dd>{{ paymentAccount }}</dd>
                </div>

                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.paymentForm }}</dt>
                  <dd>{{ paymentForm }}</dd>
                </div>
              </dl>
            </section>

            <section class="payment-receipt-detail-summary-pane payment-receipt-detail-summary-pane--operation">
              <dl class="payment-receipt-detail-list">
                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.operationNumber }}</dt>
                  <dd>{{ operationNumber }}</dd>
                </div>
              </dl>
            </section>
          </div>
        </section>

        <div v-if="canToggleSummary && showFullSummary" class="payment-receipt-detail-summary-expanded">
          <section class="payment-receipt-detail-summary-block">
            <h4>{{ text.detail.cards.fiscalSummary.sections.emitter }}</h4>
            <dl class="payment-receipt-detail-list">
              <div v-if="issuerRfc !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.rfc }}</dt>
                <dd>{{ issuerRfc }}</dd>
              </div>

              <div v-if="issuerBusinessName !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
                <dd>{{ issuerBusinessName }}</dd>
              </div>

              <div v-if="issuerTaxRegime !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.taxRegime }}</dt>
                <dd>{{ issuerTaxRegime }}</dd>
              </div>

              <div v-if="issuerFiscalResidence !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.fiscalResidence }}</dt>
                <dd>{{ issuerFiscalResidence }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="showBankDetailsOnExpanded" class="payment-receipt-detail-summary-block">
            <h4>{{ text.detail.cards.fiscalSummary.sections.bankData }}</h4>
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.beneficiaryBankRfc }}</dt>
                <dd>{{ beneficiaryBankRfc }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.beneficiaryAccount }}</dt>
                <dd>{{ beneficiaryAccount }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBank }}</dt>
                <dd>{{ orderingBankName }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBankRfc }}</dt>
                <dd>{{ orderingBankRfc }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBankAccount }}</dt>
                <dd>{{ orderingBankAccount }}</dd>
              </div>
            </dl>
          </section>
        </div>
      </article>
    </div>

    <article class="invoice-create-card invoice-create-concepts-card">
      <header class="invoice-create-card__head">
        <h3>{{ text.detail.cards.concepts.title }}</h3>
      </header>

      <div class="app-table-wrap invoice-create-concepts-table-wrap">
        <div class="app-table-scroll">
          <table class="app-data-table">
            <thead>
              <tr>
                <th>{{ text.detail.cards.concepts.table.headers.date || 'Fecha' }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.folio || 'Folio' }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.business }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.concept }}</th>
                <th class="app-text-right">{{ text.detail.cards.concepts.table.headers.amount }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="paymentConceptRows.length === 0">
                <td colspan="5" class="app-table-empty">{{ text.detail.cards.concepts.table.empty }}</td>
              </tr>

              <tr v-for="row in paymentConceptRows" :key="row.key">
                <td>
                  {{ row.dateLabel }}
                </td>
                <td>
                  <router-link
                    v-if="row.folioRoute"
                    :to="row.folioRoute"
                    class="payment-receipt-detail-link"
                  >
                    {{ row.folioLabel }}
                  </router-link>
                  <span v-else>{{ row.folioLabel }}</span>
                </td>
                <td>
                  <router-link
                    v-if="row.businessRoute"
                    :to="row.businessRoute"
                    class="payment-receipt-detail-link"
                  >
                    {{ row.businessLabel }}
                  </router-link>
                  <span v-else>{{ row.businessLabel }}</span>
                </td>
                <td>{{ row.concept }}</td>
                <td class="app-text-right"><strong>{{ row.amountLabel }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>

    <div class="invoice-create-bottom-grid">
      <RecentActivityCard
        :entity-id="paymentReceiptId"
        entity-type="paymentReceipt"
        :per-page="4"
        :show-footer="false"
      />

      <article class="invoice-create-card invoice-create-summary-card">
        <div class="invoice-create-summary-total">
          <span>{{ text.detail.cards.operation.fields.total }}</span>
          <div class="invoice-create-summary-value">
            <strong>{{ totalLabel }}</strong>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import { formatDateShort, formatDateTime, parsePresentationDate } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: paymentReceiptsText } = useDomainText('paymentReceipts');

const BANK_DETAILS_ENABLED_PAYMENT_FORMS = new Set([
  '02',
  '03',
  '04',
  '05',
  '06',
  '28',
  '29',
  '99',
]);

export default {
  name: 'PaymentReceiptDetailTabDetail',
  components: {
    RecentActivityCard,
  },
  props: {
    paymentReceipt: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text: paymentReceiptsText,
      showFullSummary: false,
    };
  },
  computed: {
    fallback() {
      return '';
    },
    paymentReceiptId() {
      return Number(this.paymentReceipt?.id || 0) || 0;
    },
    normalizedCurrency() {
      return String(this.paymentReceipt?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    normalizedExchangeRate() {
      const numeric = Number(this.paymentReceipt?.exchange_rate);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    },
    paymentFormCode() {
      return String(this.paymentReceipt?.payment_form_code || this.paymentReceipt?.payment_form || '').trim().toUpperCase();
    },
    isCfdiMode() {
      const relationType = String(this.paymentReceipt?.relation_type || this.paymentReceipt?.voucher_type || '').trim().toLowerCase();
      return relationType !== 'simple_note' && Number(this.paymentReceipt?.special || 0) !== 1;
    },
    isStampedPayment() {
      const stampedFlag = Number(this.paymentReceipt?.is_stamped || 0) || 0;
      if (stampedFlag > 0) {
        return true;
      }

      const cfdiId = Number(this.paymentReceipt?.cfdi_id || 0) || 0;
      const cfdiPagoId = Number(this.paymentReceipt?.cfdi_pago_id || 0) || 0;
      return cfdiId > 0 && cfdiPagoId > 0;
    },
    showBankFields() {
      if (!this.isCfdiMode) {
        return false;
      }

      if (this.paymentFormCode === '') {
        return false;
      }

      return BANK_DETAILS_ENABLED_PAYMENT_FORMS.has(this.paymentFormCode);
    },
    canToggleSummary() {
      return this.isCfdiMode;
    },
    summaryTitle() {
      if (this.isCfdiMode) {
        return String(this.text?.detail?.cards?.fiscalSummary?.title || 'Resumen fiscal').trim();
      }

      return String(this.text?.detail?.cards?.summary?.title || 'Resumen').trim();
    },
    summaryToggleMoreLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showAll || 'Ver todo').trim();
    },
    summaryToggleLessLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showLess || 'Ver menos').trim();
    },
    showCurrencyExchange() {
      return this.normalizedCurrency !== 'MXN' && this.normalizedExchangeRate !== null;
    },
    statusLabel() {
      return String(this.paymentReceipt?.status || '').trim() || this.fallback;
    },
    statusClass() {
      const status = String(this.paymentReceipt?.status_key || '').trim().toLowerCase();
      return {
        'payment-receipts-status-pill--active': status !== 'canceled',
        'payment-receipts-status-pill--canceled': status === 'canceled',
      };
    },
    customerRoute() {
      const customerId = Number(this.paymentReceipt?.customer_id || 0) || 0;
      if (customerId <= 0) {
        return null;
      }

      return {
        name: 'customerDetail',
        params: { customerId: String(customerId) },
      };
    },
    customerName() {
      return String(this.paymentReceipt?.customer_name || '').trim() || this.fallback;
    },
    relationLines() {
      const rawLines = Array.isArray(this.paymentReceipt?.relation_lines) ? this.paymentReceipt.relation_lines : [];
      const normalized = rawLines
        .map((raw, index) => {
          const reference = String(raw?.reference || '').trim();
          if (reference === '') {
            return null;
          }

          return {
            key: `relation-${index}-${reference}`,
            reference,
            route: this.resolveRelationRoute(raw),
          };
        })
        .filter((item) => item !== null);

      if (normalized.length > 0) {
        return normalized;
      }

      const fallbackReference = String(this.paymentReceipt?.relation || '').trim();
      if (fallbackReference === '') {
        return [];
      }

      return [{
        key: 'relation-fallback',
        reference: fallbackReference,
        route: null,
      }];
    },
    businessLines() {
      const backendRelatedBusinesses = Array.isArray(this.paymentReceipt?.related_businesses)
        ? this.paymentReceipt.related_businesses
        : [];
      const normalizedBackendRelated = backendRelatedBusinesses
        .map((raw, index) => {
          const label = String(raw?.label || '').trim();
          if (label === '') {
            return null;
          }

          const saleId = Number(raw?.sale_id || 0) || 0;
          return {
            key: `related-business-${saleId || index}-${label}`,
            label,
            businessRoute: saleId > 0
              ? {
                name: 'saleDetail',
                params: { saleId: String(saleId) },
              }
              : null,
          };
        })
        .filter((item) => item !== null);

      if (normalizedBackendRelated.length > 0) {
        return normalizedBackendRelated;
      }

      const rawLines = Array.isArray(this.paymentReceipt?.relation_lines) ? this.paymentReceipt.relation_lines : [];
      const seen = new Set();
      const normalized = rawLines
        .map((raw) => {
          const businessName = String(raw?.business_name || raw?.sale_code || raw?.sale_name || '').trim();
          if (businessName === '') {
            return null;
          }

          const saleId = Number(raw?.sale_id || 0) || 0;
          const dedupKey = saleId > 0 ? `sale-${saleId}` : `name-${businessName.toLowerCase()}`;
          if (seen.has(dedupKey)) {
            return null;
          }

          seen.add(dedupKey);

          return {
            key: dedupKey,
            label: businessName,
            businessRoute: this.resolveBusinessRoute(raw),
          };
        })
        .filter((item) => item !== null);

      if (normalized.length > 0) {
        return normalized;
      }

      const saleCode = String(this.paymentReceipt?.sale_code || '').trim();
      const saleName = String(this.paymentReceipt?.sale_name || '').trim();
      const fallbackBusiness = this.buildSaleLabel({ sale_code: saleCode, sale_name: saleName });
      if (fallbackBusiness === '') {
        return [];
      }

      return [{
        key: 'business-fallback',
        label: fallbackBusiness,
        businessRoute: this.resolveBusinessRoute(this.paymentReceipt || {}),
      }];
    },
    paymentForm() {
      return String(this.paymentReceipt?.payment_form || '').trim() || this.fallback;
    },
    hasPaymentForm() {
      return this.paymentForm !== '';
    },
    paymentAccount() {
      return String(this.paymentReceipt?.payment_account || '').trim() || this.fallback;
    },
    operationNumber() {
      return String(this.paymentReceipt?.operation_number || '').trim() || this.fallback;
    },
    sentLabel() {
      if (typeof this.paymentReceipt?.sent !== 'boolean') {
        return '-';
      }

      return this.paymentReceipt?.sent ? 'Si' : 'No';
    },
    hasSentLabel() {
      return this.sentLabel !== '';
    },
    lastSentLabel() {
      return formatDateTime(this.paymentReceipt?.last_sent_at || this.paymentReceipt?.sent_at, { fallback: '' });
    },
    hasLastSentLabel() {
      return this.lastSentLabel !== '';
    },
    totalLabel() {
      return formatAmountWithCurrency(this.paymentReceipt?.total || 0, this.paymentReceipt?.currency || 'MXN');
    },
    folio() {
      return String(this.paymentReceipt?.folio || '').trim() || this.fallback;
    },
    showFolio() {
      return this.folio !== '';
    },
    cfdiFolio() {
      const serial = String(this.paymentReceipt?.payment_cfdi_serial || '').trim();
      const folio = String(this.paymentReceipt?.payment_cfdi_folio || '').trim();
      return `${serial}${folio}`.trim() || this.fallback;
    },
    showCfdiFolio() {
      return this.isStampedPayment && this.cfdiFolio !== '';
    },
    cfdiUuid() {
      return String(this.paymentReceipt?.payment_cfdi_uuid || '').trim() || this.fallback;
    },
    showCfdiUuid() {
      return this.isStampedPayment && this.cfdiUuid !== '';
    },
    emissionDateLabel() {
      const parsed = parsePresentationDate(this.paymentReceipt?.payment_cfdi_date || this.paymentReceipt?.date);
      if (!parsed) {
        return '';
      }

      const day = new Intl.DateTimeFormat('es-MX', { day: '2-digit' }).format(parsed);
      const month = new Intl.DateTimeFormat('es-MX', { month: 'long' }).format(parsed);
      const year = new Intl.DateTimeFormat('es-MX', { year: 'numeric' }).format(parsed);
      const time = new Intl.DateTimeFormat('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(parsed).replace(',', '');

      return `${day} de ${month} de ${year} a las ${time}`;
    },
    showEmissionDate() {
      return this.emissionDateLabel !== '';
    },
    currencyExchange() {
      if (!this.showCurrencyExchange) {
        return '';
      }

      return `${this.normalizedCurrency} / ${this.normalizedExchangeRate}`;
    },
    beneficiaryAccount() {
      const beneficiary = String(this.paymentReceipt?.bank_account_number || this.paymentReceipt?.cta_beneficiario || '').trim();
      const ordering = String(this.paymentReceipt?.cta_ordenante || '').trim();
      return beneficiary || ordering || this.fallback;
    },
    beneficiaryBankRfc() {
      return String(this.paymentReceipt?.rfc_emisor_cta_ben || this.paymentReceipt?.rfc_banco_beneficiario || '').trim() || this.fallback;
    },
    orderingBankName() {
      return String(this.paymentReceipt?.nom_banco_ord_ext || this.paymentReceipt?.bank_name || '').trim() || this.fallback;
    },
    orderingBankRfc() {
      return String(this.paymentReceipt?.rfc_emisor_cta_ord || '').trim() || this.fallback;
    },
    orderingBankAccount() {
      return String(this.paymentReceipt?.cta_ordenante || '').trim() || this.fallback;
    },
    issuerRfc() {
      const emissionMeta = this.paymentReceipt?.emission_trace?.meta;
      return String(
        this.paymentReceipt?.issuer_rfc
        || this.paymentReceipt?.emitter_rfc
        || emissionMeta?.emitter?.rfc
        || ''
      ).trim() || this.fallback;
    },
    issuerBusinessName() {
      const emissionMeta = this.paymentReceipt?.emission_trace?.meta;
      return String(
        this.paymentReceipt?.issuer?.business_name
        || this.paymentReceipt?.issuer?.name
        || this.paymentReceipt?.issuer_name
        || this.paymentReceipt?.emitter_name
        || emissionMeta?.emitter?.name
        || ''
      ).trim() || this.fallback;
    },
    issuerTaxRegime() {
      return String(
        this.paymentReceipt?.issuer?.tax_regime_label
        || this.paymentReceipt?.issuer?.tax_regime
        || this.paymentReceipt?.issuer?.regimen
        || ''
      ).trim() || this.fallback;
    },
    issuerFiscalResidence() {
      return String(
        this.paymentReceipt?.issuer?.fiscal_residence
        || this.paymentReceipt?.issuer?.zipcode
        || ''
      ).trim() || this.fallback;
    },
    receiverRfc() {
      return String(this.paymentReceipt?.receiver?.rfc || this.paymentReceipt?.receiver_rfc || '').trim() || this.fallback;
    },
    receiverBusinessName() {
      return String(
        this.paymentReceipt?.receiver?.business_name
        || this.paymentReceipt?.receiver_business_name
        || this.paymentReceipt?.customer_name
        || ''
      ).trim() || this.fallback;
    },
    showReceiverRfc() {
      return this.isStampedPayment && this.receiverRfc !== '';
    },
    showReceiverBusinessName() {
      return this.isStampedPayment && this.receiverBusinessName !== '';
    },
    showBankDetailsOnExpanded() {
      return this.isStampedPayment && this.showBankFields;
    },
    paymentConceptRows() {
      const lines = Array.isArray(this.paymentReceipt?.relation_lines) ? this.paymentReceipt.relation_lines : [];

      return lines
        .map((line, index) => {
          const amount = Number(line?.amount || 0);
          if (!Number.isFinite(amount) || amount <= 0) {
            return null;
          }

          const backendBusinessLabel = String(line?.business_name || '').trim();
          const saleLabel = backendBusinessLabel || this.buildSaleLabel(line) || this.fallback;
          const isGenericBusiness = this.isGenericBusinessLabel(saleLabel, line);
          const reference = String(line?.reference || '').trim();
          const concept = String(line?.concept || '').trim() || this.fallback;
          const currency = String(line?.currency || this.normalizedCurrency).trim().toUpperCase() || this.normalizedCurrency;
          const dateLabel = formatDateShort(line?.date || '', { fallback: '-' });
          const folioLabel = String(line?.folio || reference || '').trim() || this.fallback;
          const relationRoute = this.resolveRelationRoute(line);

          return {
            key: `concept-${index}-${reference || saleLabel || amount}`,
            dateLabel,
            folioLabel,
            folioRoute: relationRoute,
            businessLabel: isGenericBusiness ? 'Sin negocio relacionado' : saleLabel,
            businessRoute: isGenericBusiness ? null : this.resolveBusinessRoute(line),
            concept,
            amountLabel: formatAmountWithCurrency(amount, currency),
          };
        })
        .filter((item) => item !== null);
    },
  },
  methods: {
    buildSaleLabel(source) {
      const code = String(source?.sale_code || '').trim();
      const name = String(source?.sale_name || '').trim();

      if (code !== '' && name !== '') {
        return `${code} - ${name}`;
      }

      if (code !== '') {
        return code;
      }

      return name;
    },
    isGenericBusinessLabel(label, source = {}) {
      const normalizedLabel = String(label || '').trim().toLowerCase();
      const normalizedCode = String(source?.sale_code || '').trim().toLowerCase();
      const normalizedName = String(source?.sale_name || '').trim().toLowerCase();

      return normalizedLabel === 'sin negocio relacionado'
        || normalizedCode === 'generic'
        || normalizedCode === 'reference generic'
        || normalizedName === 'generic'
        || normalizedName === 'reference generic';
    },
    resolveSaleRoute(source) {
      const saleId = Number(source?.sale_id || 0) || 0;
      if (saleId <= 0) {
        return null;
      }

      return {
        name: 'saleDetail',
        params: { saleId: String(saleId) },
      };
    },
    resolveBusinessRoute(source) {
      const directRoute = this.resolveSaleRoute(source);
      if (directRoute) {
        return directRoute;
      }

      return this.resolveSaleRoute(this.paymentReceipt || {});
    },
    resolveRelationRoute(source) {
      const invoiceId = Number(source?.invoice_id || 0) || 0;
      if (invoiceId > 0) {
        return {
          name: 'invoiceDetail',
          params: { invoiceId: String(invoiceId) },
        };
      }

      return this.resolveSaleRoute(source);
    },
  },
};
</script>

<style scoped>
.payment-receipt-detail-top-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.payment-receipt-detail-card {
  height: 100%;
}

.payment-receipt-detail-operation-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 16px;
}

.payment-receipt-detail-operation-pane {
  min-width: 0;
}

.payment-receipt-detail-operation-pane--totals {
  border-left: 1px solid rgba(148, 163, 184, 0.32);
  padding-left: 14px;
}

.payment-receipt-detail-list {
  margin: 0;
  padding: 0;
}

.payment-receipt-detail-row {
  display: grid;
  grid-template-columns: minmax(130px, auto) minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 8px;
}

.payment-receipt-detail-row--dual {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-receipt-detail-row--dual > div dt {
  display: inline;
  margin: 0;
  font-size: 11px;
  color: #7b879a;
  font-weight: 600;
}

.payment-receipt-detail-row--dual > div dd {
  display: inline;
  margin: 0;
  margin-left: 8px;
  font-size: 12px;
  color: #334155;
  font-weight: 600;
  word-break: break-word;
}

.payment-receipt-detail-row--separator {
  border-bottom: 1px solid rgba(148, 163, 184, 0.32);
  padding-bottom: 8px;
  margin-bottom: 10px;
}

.payment-receipt-detail-row--multiline dd {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-receipt-detail-row--total {
  border-top: 1px solid rgba(148, 163, 184, 0.32);
  padding-top: 10px;
  margin-top: 14px;
}

.payment-receipt-detail-row--total dd {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.payment-receipt-detail-row--total-highlight {
  margin-top: 4px;
  padding: 14px 12px;
  border: 1px solid rgba(14, 116, 144, 0.2);
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(236, 254, 255, 0.9) 0%, rgba(240, 249, 255, 0.95) 100%);
}

.payment-receipt-detail-row--total-highlight dt {
  color: #0f766e;
  font-size: 12px;
}

.payment-receipt-detail-row--total-highlight dd {
  color: #0f172a;
  font-size: 18px;
}

.payment-receipt-detail-row dt {
  margin: 0;
  font-size: 11px;
  color: #7b879a;
  font-weight: 600;
}

.payment-receipt-detail-row dd {
  margin: 0;
  font-size: 12px;
  color: #334155;
  font-weight: 600;
  word-break: break-word;
}

.payment-receipt-detail-multiline-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-receipt-detail-link {
  color: #0070cd;
  font-weight: 600;
  text-decoration: none;
}

.payment-receipt-detail-link:hover {
  color: #005a9f;
  text-decoration: underline;
}

.payment-receipts-status-pill--active {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.payment-receipts-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}

.payment-receipt-detail-summary-toggle {
  border: 0;
  background: transparent;
  padding: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.payment-receipt-detail-summary-toggle:hover {
  color: #005a9f;
}

.payment-receipt-detail-summary-expanded {
  border-top: 1px solid rgba(148, 163, 184, 0.34);
  margin-top: 12px;
  padding-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-receipt-detail-summary-block h4 {
  margin: 0 0 8px;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.payment-receipt-detail-summary-payment-block {
  border-top: 1px solid rgba(148, 163, 184, 0.34);
  margin-top: 12px;
  padding-top: 12px;
}

.payment-receipt-detail-summary-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 14px;
}

.payment-receipt-detail-summary-pane {
  min-width: 0;
}

.payment-receipt-detail-summary-pane--operation {
  border-left: 1px solid rgba(148, 163, 184, 0.32);
  padding-left: 14px;
}

@media (max-width: 1100px) {
  .payment-receipt-detail-top-grid {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-operation-split {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-operation-pane--totals {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.32);
    padding-left: 0;
    padding-top: 10px;
  }

  .payment-receipt-detail-row--dual {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .payment-receipt-detail-summary-expanded {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-summary-split {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .payment-receipt-detail-summary-pane--operation {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.32);
    padding-left: 0;
    padding-top: 10px;
  }
}
</style>
